// Picks how many pixels the world pass is allowed to cost, from measured frame
// time. This is deliberately a view-layer concern: nothing here is ever written
// back into the sim, so the governor cannot change how the game plays, only how
// it looks. Note what it is deliberately NOT wired to: `prefersReducedMotion`.
// That is a statement about motion, not about the GPU, and resolution is not
// motion -- it stays doing exactly what it already did.
//
// Budgets are pixel counts rather than device-pixel-ratios so the ladder means
// the same thing on a small cheap phone and a large tablet: a DPR of 2 is a
// very different amount of work on a 360pt screen than on a 1024pt one.
export const PIXEL_BUDGETS = [320_000, 480_000, 700_000, 1_000_000, 1_400_000] as const;

export const TOP_LEVEL = PIXEL_BUDGETS.length - 1;

// The deadband between these two numbers is the whole trick. A single
// threshold makes the governor oscillate: it steps down, the frame gets
// cheaper, the same threshold now says step up, and it ping-pongs forever.
// Two thresholds leave a band where it does nothing at all.
//
// The values are set for what we can actually measure. All we have is the
// wall-clock interval between renders, which vsync pins to the refresh
// period -- a perfectly healthy frame on a 60Hz panel reads 16.7ms no matter
// how much headroom the GPU has, so a threshold pair like 18/12.5 (which
// assumes GPU time) would never let a 60Hz display climb at all. These are
// framed as "did we hold roughly 48fps" and "are we comfortably past 58fps",
// which behaves correctly at 60Hz and at 120Hz.
export const STEP_DOWN_MS = 21;
export const STEP_UP_MS = 17.2;

// Asymmetric in time as well as in threshold. Dropping quality briefly when it
// was not needed is invisible; holding a level that stutters is not.
const UP_HOLD_MS = 2500;
const COOLDOWN_MS = 1000;
const WINDOW_FRAMES = 45;
const WARMUP_FRAMES = 60;
const MAX_REVERSALS = 3;

// A frame this long is a tab switch, a GC pause or a breakpoint, not evidence
// about the GPU.
const OUTLIER_MS = 100;

export type Governor = {
  /** Feed one rendered frame's duration. */
  sample: (dtMs: number) => void;
  /** Forget the window -- after a tab switch, or a resize that changes cost. */
  reset: () => void;
  level: () => number;
  budget: () => number;
  /** True once the governor has given up climbing for this session. */
  latched: () => boolean;
};

export type GovernorOptions = {
  startLevel?: number;
  /** Ceiling the governor may never climb past. */
  maxLevel?: number;
};

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const i = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[i]!;
}

export function createGovernor(opts: GovernorOptions = {}): Governor {
  const maxLevel = Math.min(TOP_LEVEL, opts.maxLevel ?? TOP_LEVEL);
  let level = Math.max(0, Math.min(maxLevel, opts.startLevel ?? maxLevel));

  const window: number[] = [];
  let seen = 0;
  let cooldown = 0;
  let goodFor = 0;
  // Counts direction changes, not steps. A governor that keeps reversing has
  // found a level boundary that sits exactly on the device's capability, and
  // the honest answer there is to stay on the cheap side of it.
  let reversals = 0;
  let lastDir = 0;
  let latch = false;

  const change = (dir: -1 | 1) => {
    level = Math.max(0, Math.min(maxLevel, level + dir));
    if (lastDir !== 0 && dir !== lastDir) reversals += 1;
    lastDir = dir;
    if (reversals >= MAX_REVERSALS) latch = true;
    window.length = 0;
    cooldown = COOLDOWN_MS;
    goodFor = 0;
  };

  return {
    sample(dtMs) {
      if (!Number.isFinite(dtMs) || dtMs <= 0 || dtMs > OUTLIER_MS) return;

      // Shader compilation, texture upload and the GPU's own clock ramp make
      // the opening frames wildly unrepresentative -- a trivial shader can
      // measure two orders of magnitude over its warm cost.
      seen += 1;
      if (seen <= WARMUP_FRAMES) return;

      if (cooldown > 0) {
        cooldown -= dtMs;
        return;
      }

      window.push(dtMs);
      if (window.length > WINDOW_FRAMES) window.shift();
      if (window.length < WINDOW_FRAMES) return;

      // p90, not the mean: one 40ms GC pause should not drag the average into
      // a downgrade, but a frame that is late one time in ten should.
      const p90 = percentile(
        [...window].sort((a, b) => a - b),
        0.9,
      );

      if (p90 > STEP_DOWN_MS) {
        if (level > 0) change(-1);
        else window.length = 0;
        return;
      }

      if (p90 < STEP_UP_MS && !latch && level < maxLevel) {
        goodFor += dtMs;
        if (goodFor >= UP_HOLD_MS) change(1);
        return;
      }

      goodFor = 0;
    },
    reset() {
      window.length = 0;
      goodFor = 0;
      cooldown = COOLDOWN_MS;
    },
    level: () => level,
    budget: () => PIXEL_BUDGETS[level]!,
    latched: () => latch,
  };
}

/**
 * Render scale for a budget, as a fraction of the full-resolution world pass.
 * Never above 1: the render target is allocated at full size and the world is
 * drawn into a sub-rect of it, so 1 is "exactly as today".
 */
export function scaleForBudget(budget: number, deviceW: number, deviceH: number): number {
  const px = Math.max(1, deviceW * deviceH);
  return Math.max(0.5, Math.min(1, Math.sqrt(budget / px)));
}

/**
 * Where to start before any frame has been measured. A touch device is assumed
 * to be the weaker one and climbs if it earns it; guessing high and stuttering
 * for the first two seconds reads as a broken game, guessing low does not.
 *
 * A software renderer starts at the very bottom. It reports every capability
 * a real GPU does -- float colour buffers, a fine pointer -- and then
 * rasterises a half-float multisampled target on the CPU at a frame rate the
 * governor needs seconds of warmup and window to react to. Headless Chromium
 * is one; a VM or a remote desktop with no GPU passthrough is another.
 */
export function startLevelFor(coarsePointer: boolean, softwareRenderer = false): number {
  if (softwareRenderer) return 0;
  return coarsePointer ? 1 : TOP_LEVEL;
}

/** True for the renderer strings that mean "no GPU is drawing this". */
export function isSoftwareRenderer(renderer: string | null | undefined): boolean {
  return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(renderer ?? "");
}

let fullResolution = false;

export function setFullResolution(on: boolean): void {
  fullResolution = on;
}

export function isFullResolution(): boolean {
  return fullResolution;
}
